import React, { useState, useEffect, useMemo } from "react";
import dayjs from "dayjs";
import { DatePickerComponent } from "@syncfusion/ej2-react-calendars";

import { useEpgData } from "../../hooks/useEpgData";
import { useImpressionsData } from "../../hooks/useImpressionsData";
import { useUniqueTvData } from "../../hooks/useUniqueTvData";
import { useSocialMediaData } from "../../hooks/useSocialMediaData";
import { useChannelData } from "../../hooks/useChannelData";
import { useManagementReportEngine } from "../../hooks/useManagementReportEngine";
import { useUserJourneyLogger } from "../../hooks/useUserJourneyLogger";

import { exportManagementReportPdf } from "../../utils/exportManagementReportPdf";
import {
  NONE,
  allowedFeatures,
  availableChartOptions,
  availableTableColumns,
  availableTotals,
  availableValues,
  defaultReportConfig,
  hasTotals,
  isReportEmpty,
} from "../../utils/managementReportConfig";
import PieChart from "../../components/charts3/PieChart";
import "./ChannelManagementReport.css";

export default function ChannelManagementReport({
  channelId,
  userSidebarItems = [],
  channelSidebarItems = [],
}) {
  const [selectedDate, setSelectedDate] = useState("");

  // What this customer is entitled to put in the report — the same user/channel
  // intersection the sidebar uses, so nothing they cannot see is on offer.
  const features = useMemo(
    () => allowedFeatures(userSidebarItems, channelSidebarItems),
    [userSidebarItems, channelSidebarItems]
  );
  const chartOptions = useMemo(() => availableChartOptions(features), [features]);
  const columnOptions = useMemo(() => availableTableColumns(features), [features]);
  const totalsOptions = useMemo(() => availableTotals(features), [features]);
  const valueOptions = useMemo(() => availableValues(features), [features]);

  const [config, setConfig] = useState(() => defaultReportConfig(features));
  useEffect(() => setConfig(defaultReportConfig(features)), [features]);

  // Drawing a pie the report will not use is wasted work, and the export waits
  // on these being ready before it starts.
  const needsChart = (slot) => config.chart1 === slot || config.chart2 === slot;

  // What the current choices add up to, so the shape of the PDF is visible
  // before it is generated rather than only after opening the file.
  const summary = useMemo(() => {
    const pages = [];
    [config.chart1, config.chart2].forEach((slot) => {
      const found = chartOptions.find((o) => o.value === slot);
      if (found) pages.push(found.label);
    });

    const lastPage = [];
    if (config.tableEnabled) {
      const names = columnOptions
        .filter((c) => config.tableColumns.includes(c.value))
        .map((c) => c.label);
      lastPage.push(`Program tablosu (${names.join(", ")})`);
    }
    // Totals are built from the table's own columns, so with all of the
    // totalled ones switched off the period boxes would promise a section the
    // PDF cannot produce.
    const periods = totalsOptions
      .filter((t) => config.totals.includes(t.value))
      .map((t) => t.label.replace(" toplamlar", ""));
    if (periods.length && hasTotals(config)) {
      lastPage.push(`${periods.join(" + ")} toplamlar`);
    }

    const values = valueOptions
      .filter((v) => (config.values || []).includes(v.value))
      .map((v) => v.label);
    if (values.length) lastPage.push(values.join(" · "));

    if (lastPage.length) pages.push(lastPage.join(" · "));
    return pages;
  }, [config, chartOptions, columnOptions, totalsOptions, valueOptions]);

  const setSlot = (key, value) => setConfig((c) => ({ ...c, [key]: value }));
  const toggleInList = (key, value) =>
    setConfig((c) => ({
      ...c,
      [key]: c[key].includes(value)
        ? c[key].filter((v) => v !== value)
        : [...c[key], value],
    }));
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const storedUser = JSON.parse(localStorage.getItem("adm_panel_user") || "{}");
  const isDark = !!storedUser.darkmode;

  const colors = useMemo(
    () => ({
      pageBg: "transparent",
      cardBg: isDark ? "#1e293b" : "#ffffff",
      text: isDark ? "#f1f5f9" : "#0b5ed7",
      subText: isDark ? "#cbd5f5" : "#666",
      border: isDark ? "#334155" : "#e5e7eb",
      inputBg: isDark ? "#020617" : "#ffffff",
      shadow: isDark ? "none" : "0 10px 30px rgba(0,0,0,0.06)",
    }),
    [isDark]
  );

  const { channelInfo, rawVideo } = useChannelData(channelId);
  const { rawImpressions } = useImpressionsData(channelId);

  // Average video consumption is viewing time per unique television, so the
  // day's unique count is part of the figure rather than a nice-to-have.
  const { data: uniqueTvData } = useUniqueTvData(
    channelId,
    selectedDate || null,
    selectedDate || null
  );

  // Fetch EPG covering the selected date + last 30 days for recurring schedules
  const epgStartDate = useMemo(() => {
    const thirtyDaysAgo = dayjs().subtract(30, "day");
    if (selectedDate) {
      const sd = dayjs(selectedDate);
      return (sd.isBefore(thirtyDaysAgo) ? sd : thirtyDaysAgo).format("YYYY-MM-DD");
    }
    return thirtyDaysAgo.format("YYYY-MM-DD");
  }, [selectedDate]);
  const epgEndDate = useMemo(() => dayjs().format("YYYY-MM-DD"), []);
  const { epgBlocks, loading: epgLoading } = useEpgData(channelId, null, {
    startDateRange: epgStartDate,
    endDateRange:   epgEndDate,
  });

  const { youtubeData, twitterData } = useSocialMediaData(
    channelId,
    "minute",
    selectedDate,
    selectedDate,
    selectedDate,
    channelInfo?.ytChannelId || null
  );

  const report = useManagementReportEngine({
    selectedDate,
    rawImpressions,
    rawVideo,
    uniqueTvData,
    twitterData,
    youtubeData,
    epgBlocks,
  });

  const rows = report?.rows || [];
  const totals = report?.totals;

// A chart whose canvas is gone has been destroyed and cannot be drawn from.
// That happens whenever the report recomputes — data arriving late is enough —
// so a reference read a moment ago may already be dead.
const liveChart = (chart) => (chart?.canvas ? chart : null);

// Everything that differs between the pie charts, in one place: which global
// the rendered chart registers itself under, which ranking it draws, and which
// field decides the slice sizes. Adding a fourth ranking is one entry here.
const CHART_SLOTS = {
  topWatched: {
    globalId: "impPieChart",
    rowsKey: "sortedImpTop10",
    valueKey: "totalvalue",
  },
  topActiveTv: {
    globalId: "activeTvPieChart",
    rowsKey: "sortedActiveTvTop10",
    valueKey: "avgActiveTv",
  },
  topSwitchIn: {
    globalId: "avgPieChart",
    rowsKey: "sortedSwitchTop10",
    valueKey: "value",
  },
};

const handleExportPDF = async () => {
  if (!report?.ready || loading) return;

  // Only wait on the charts this report actually includes — an unselected one
  // is never drawn, so requiring it would block the export for good.
  const missingChart = Object.entries(CHART_SLOTS).some(
    ([slot, { globalId }]) => needsChart(slot) && !liveChart(window[globalId])
  );

  if (missingChart) {
    alert("Grafikler hazırlanıyor, lütfen 1–2 saniye sonra tekrar deneyin.");
    return;
  }

  setLoading(true);
  setProgress(5);

  try {

    await new Promise(r => setTimeout(r, 250));
    setProgress(20);

    // Read again after the wait: the charts may have been torn down and
    // rebuilt while it ran, which is what made the first export fail.
    const charts = Object.fromEntries(
      Object.entries(CHART_SLOTS).map(([slot, { globalId }]) => [
        slot,
        liveChart(window[globalId]),
      ])
    );

    await exportManagementReportPdf({
      channelName: channelInfo?.name || "Report",
      selectedDate: dayjs(selectedDate).format("DD/MM/YYYY"),
      rows,
      totals,
      charts,
      config,
      onProgress: (p) => {
        setProgress(20 + (p * 0.8));
      }
    });

    setProgress(100);

    setTimeout(() => {
      setLoading(false);
      setProgress(0);
    }, 400);

  } catch (e) {
    console.error("PDF export failed:", e);
    alert("PDF oluşturulurken hata oluştu. Console'u kontrol edin.");
    setLoading(false);
    setProgress(0);
  }
};


  const { logPageChange } = useUserJourneyLogger({
    channelId,
  });

  useEffect(() => {
    logPageChange({
      chartType: "managementreport",
    });
  }, [logPageChange]);

  return (
    <>
      <div
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          paddingTop: 70,
          paddingBottom: 80,
          background: colors.pageBg,
        }}
      >
        <div
          style={{
            width: 760,
            background: colors.cardBg,
            borderRadius: 14,
            padding: "48px 44px",
            textAlign: "center",
            boxShadow: colors.shadow,
            border: `1px solid ${colors.border}`,
            color: colors.text,
          }}
        >
          <h2
            style={{
              color: colors.text,
              marginBottom: 8,
              fontWeight: 700,
            }}
          >
            {channelInfo?.name
              ? `${channelInfo.name} – Management Report`
              : "Management Report"}
          </h2>

          <div
            style={{
              color: colors.subText,
              marginBottom: 36,
              fontSize: 14,
            }}
          >
            Smart TV & Social Media Report Export
          </div>

          {/* -------- What goes into the report -------- */}

          <div className="mgmt-config" style={{ borderColor: colors.border }}>
            <div className="mgmt-config-title" style={{ color: colors.text }}>
              Rapor İçeriği
            </div>

            <div className="mgmt-config-grid">
              {[
                ["chart1", "1. Grafik Sayfası"],
                ["chart2", "2. Grafik Sayfası"],
              ].map(([key, label]) => (
                <label key={key} className="mgmt-config-field">
                  <span style={{ color: colors.subText }}>{label}</span>
                  <select
                    value={config[key]}
                    onChange={(e) => setSlot(key, e.target.value)}
                    style={{
                      background: colors.inputBg,
                      color: colors.text,
                      borderColor: colors.border,
                    }}
                  >
                    <option value={NONE}>— Rapora ekleme —</option>
                    {chartOptions.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </label>
              ))}
            </div>

            <div className="mgmt-config-row">
              <label className="mgmt-config-check" style={{ color: colors.text }}>
                <input
                  type="checkbox"
                  checked={config.tableEnabled}
                  onChange={(e) => setSlot("tableEnabled", e.target.checked)}
                />
                Program tablosu
              </label>

              {config.tableEnabled &&
                columnOptions.map((c) => (
                  <label
                    key={c.value}
                    className="mgmt-config-check"
                    style={{ color: colors.subText }}
                  >
                    <input
                      type="checkbox"
                      checked={config.tableColumns.includes(c.value)}
                      disabled={c.always}
                      onChange={() => toggleInList("tableColumns", c.value)}
                    />
                    {c.label}
                  </label>
                ))}
            </div>

            <div className="mgmt-config-row">
              {totalsOptions.map((t) => (
                <label
                  key={t.value}
                  className="mgmt-config-check"
                  style={{ color: colors.subText }}
                >
                  <input
                    type="checkbox"
                    checked={config.totals.includes(t.value)}
                    onChange={() => toggleInList("totals", t.value)}
                  />
                  {t.label}
                </label>
              ))}

              {valueOptions.map((v) => (
                <label
                  key={v.value}
                  className="mgmt-config-check"
                  style={{ color: colors.subText }}
                >
                  <input
                    type="checkbox"
                    checked={(config.values || []).includes(v.value)}
                    onChange={() => toggleInList("values", v.value)}
                  />
                  {v.label}
                </label>
              ))}
            </div>
            <div className="mgmt-config-summary" style={{ color: colors.subText }}>
              {summary.length === 0 ? (
                <span className="mgmt-config-warn">
                  Rapora hiçbir bölüm seçilmedi.
                </span>
              ) : (
                <>
                  <strong style={{ color: colors.text }}>
                    {summary.length} sayfa
                  </strong>
                  <ol>
                    {summary.map((line, i) => (
                      <li key={i}>{line}</li>
                    ))}
                  </ol>
                </>
              )}
            </div>
          </div>

          {/* -------- Date + Button -------- */}

          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 16,
              flexWrap: "wrap",
            }}
          >
            <DatePickerComponent
              cssClass={isDark ? "mgmt-dark-datepicker" : ""}
              value={selectedDate ? dayjs(selectedDate).toDate() : null}
              max={new Date()}
              format="dd/MM/yyyy"
              placeholder="Select date"
              change={(e) => {
                if (e?.value) {
                  setSelectedDate(dayjs(e.value).format("YYYY-MM-DD"));
                }
              }}
            />

            <button
              onClick={handleExportPDF}
              disabled={loading || !report?.ready || isReportEmpty(config)}
              style={{
                minWidth: 160,
                padding: "12px 20px",
                fontSize: 14,
                fontWeight: 600,
                borderRadius: 10,
                border: "none",
                background: loading
                  ? "#9ca3af"
                  : report?.ready
                  ? "linear-gradient(135deg, #2563eb, #0b5ed7)"
                  : "#c7c7c7",
                color: "#fff",
                cursor:
                  loading || !report?.ready ? "not-allowed" : "pointer",
                boxShadow:
                  loading || isDark
                    ? "none"
                    : "0 6px 14px rgba(37,99,235,0.35)",
                transition: "all 0.2s ease",
              }}
            >
              {loading ? "PDF hazırlanıyor..." : "EXPORT PDF"}
            </button>
          </div>

          {/* -------- Status -------- */}

          {selectedDate && !report?.ready && (
            <div style={{ marginTop: 22, color: colors.subText, fontSize: 13 }}>
              {epgLoading
                ? "EPG verisi yükleniyor..."
                : !rawImpressions
                ? "İzlenme verisi yükleniyor..."
                : !epgBlocks?.length
                ? "Bu kanal için EPG verisi bulunamadı."
                : "Seçilen tarih için program bulunamadı."}
            </div>
          )}

          {loading && (
            <div style={{ marginTop: 26 }}>
              <div
                style={{
                  height: 6,
                  width: "100%",
                  background: isDark ? "#020617" : "#eef2ff",
                  borderRadius: 20,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: `${progress}%`,
                    background: "linear-gradient(90deg,#2563eb,#60a5fa)",
                    transition: "width 0.35s ease",
                  }}
                />
              </div>

              <div
                style={{
                  marginTop: 10,
                  fontSize: 12,
                  color: colors.subText,
                }}
              >
                Grafikler hazırlanıyor, PDF oluşturuluyor… ({Math.round(progress)}%)
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ================= HIDDEN PDF CHARTS ================= */}

      <div
        style={{
          position: "fixed",
          left: "-10000px",
          top: 0,
          width: 1,
          height: 1,
          overflow: "hidden",
        }}
      >
        {report?.ready &&
          Object.entries(CHART_SLOTS).map(
            ([slot, { globalId, rowsKey, valueKey }]) =>
              needsChart(slot) && (
                <PieChart
                  key={slot}
                  id={globalId}
                  rows={report[rowsKey]}
                  valueKey={valueKey}
                />
              )
          )}
      </div>

      <style>
        {`
          @keyframes loadingMove {
            0% { background-position: 0% 50%; }
            100% { background-position: 200% 50%; }
          }

          /* Syncfusion DatePicker – dark mode fix */
          .mgmt-dark-datepicker .e-input-group,
          .mgmt-dark-datepicker .e-input-group:not(.e-float-icon-left) {
            background: #020617 !important;
            border-color: #334155 !important;
          }

          .mgmt-dark-datepicker input.e-input {
            background: #020617 !important;
            color: #e5e7eb !important;
          }

          .mgmt-dark-datepicker .e-input-group-icon {
            color: #cbd5f5 !important;
          }
        `}
      </style>
    </>
  );
}
